import type { LifetimeStats, SessionRecord } from '../data/types';
import { CATEGORY_LABELS } from '../data/types';
import styles from './Welcome.module.css';

interface WelcomeProps {
  bestAccuracy: number | null;
  lastSession: SessionRecord | null;
  lifetime: LifetimeStats;
  onBegin: () => void;
}

export function Welcome({ bestAccuracy, lastSession, lifetime, onBegin }: WelcomeProps) {
  // Pick the two pattern categories with the worst lifetime accuracy
  // among ones the visitor has actually been exposed to. Need a
  // meaningful sample size before surfacing — at least 3 exposures.
  const stubbornPatterns = Object.entries(lifetime.byCategory)
    .filter(([, c]) => c.right + c.wrong >= 3)
    .map(([cat, c]) => ({
      cat: cat as keyof typeof CATEGORY_LABELS,
      missRate: c.wrong / (c.right + c.wrong),
      seen: c.right + c.wrong,
    }))
    .sort((a, b) => b.missRate - a.missRate)
    .slice(0, 2)
    .filter((p) => p.missRate > 0.2);

  const lifetimeAccuracy =
    lifetime.totalAnswered > 0 ? lifetime.totalCorrect / lifetime.totalAnswered : null;

  return (
    <section className={styles.welcome}>
      <h1 className={styles.heading}>red flag, green flag</h1>
      <p className={styles.subtitle}>learn the patterns, in play</p>
      <p className={styles.body}>
        you'll see scenario cards — profiles, messages, behaviors, quotes —
        and decide if each is a green flag (healthy) or a red flag (warning).
        immediate feedback. nothing is graded; every miss is a lesson.
      </p>
      <p className={styles.note}>
        swipe left for red, swipe right for green. arrow keys work too.
      </p>
      <button type="button" className={styles.begin} onClick={onBegin}>
        {lifetime.roundsPlayed === 0 ? 'begin' : 'play another round'}
      </button>

      {bestAccuracy !== null && lastSession && (
        <div className={styles.stats}>
          <p>
            your best: <strong>{Math.round(bestAccuracy * 100)}%</strong>
          </p>
          <p>
            last round: {lastSession.correct} of {lastSession.total} correct
          </p>
        </div>
      )}

      {lifetime.roundsPlayed > 0 && (
        <div className={styles.lifetime}>
          <p className={styles.lifetimeHeading}>across all your rounds</p>
          <p className={styles.lifetimeLine}>
            <strong>{lifetime.roundsPlayed}</strong> round
            {lifetime.roundsPlayed === 1 ? '' : 's'} played ·{' '}
            <strong>{lifetime.totalAnswered}</strong> card
            {lifetime.totalAnswered === 1 ? '' : 's'} read
            {lifetimeAccuracy !== null && (
              <>
                {' '}· <strong>{Math.round(lifetimeAccuracy * 100)}%</strong> overall
              </>
            )}
          </p>

          {stubbornPatterns.length > 0 && (
            <>
              <p className={styles.stubbornLead}>patterns you keep missing:</p>
              <ul className={styles.stubbornList}>
                {stubbornPatterns.map((p) => (
                  <li key={p.cat}>
                    <span className={styles.stubbornName}>{CATEGORY_LABELS[p.cat]}</span>
                    <span className={styles.stubbornMeta}>
                      {Math.round(p.missRate * 100)}% missed · seen {p.seen}×
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
